/*
 * Copyright (C) 2019-2022 Corvalent Corporation - All Rights Reserved
 * This software is licensed program property and is Proprietary and Confidential.
 * Unauthorized copying of this file via any medium is strictly prohibited unless
 * prior written permission has been obtained by Corvalent Corporation.
 * In addition, the look and feel of this software as well as the user interface
 * design is also Copyright ©.
 * Notice: This software is not open source.
 * This software is subject to the Corvalent Software License Agreement.
 * You should have received a copy of this license with the software.
 * If not, please write to: sales@corvalent.com
 * All information contained herein is, and remains, the property of
 * Corvalent Corporation and its suppliers, if any. This software may also
 * be covered by U.S. and Foreign Patents, patents in progress, and are
 * protected by trade secret or copyright law
 */

import { FileStatus } from 'filepond';
import Ajv from 'ajv/dist/2020';
import addFormats from 'ajv-formats';
import validateFullThresholds from './validateThresholds';
import validateBinaryIndicators from './validateBinaryIndicators';
import i18next from '../../services/i18n';
import nimbelinkConfigSchema from './nimbelinkConfigSchema.json';

export const assetTypeValidation = values => {
  const validBool = ['true', 'false'];
  const errors = {};
  if (!values.name.length) errors.name = i18next.t('validation.required');
  if (!values.user_group.length)
    errors.user_group = i18next.t('validation.required');
  if (
    typeof values.is_indoor !== 'boolean' &&
    !validBool.includes(values.is_indoor)
  )
    errors.is_indoor = i18next.t('validation.required');
  if (
    typeof values.is_moving !== 'boolean' &&
    !validBool.includes(values.is_moving)
  )
    errors.is_moving = i18next.t('validation.required');
  if (!values.icon) errors.icon = i18next.t('validation.required');
  if (!values.image || values.image.status !== FileStatus.IDLE)
    errors.image = i18next.t('validation.required');
  return errors;
};

export const assetValidation = values => {
  const errors = {};
  if (!values.name.length) errors.name = i18next.t('validation.required');
  if (!values.asset_type.length)
    errors.asset_type = i18next.t('validation.required');
  if (!values.image || values.image.status !== FileStatus.IDLE)
    errors.image = i18next.t('validation.required');
  return errors;
};

export const timeoutRegex = /^(?:[1-9]\d*)([dhms])$/;
export const numberPartRegex = /^[1-9]\d*/;
const idRegex = /^[a-zA-Z0-9_:-]{3,50}$/;
const parameterRegex = /^(?!\d)\w+$/;

export const validateSensorId = id => {
  if (!idRegex.test(id))
    return i18next.t(
      'management_menu.asset_wizard.sensor.validation.id_format',
    );
};

export const validateTimeout = timeout => {
  if (!timeout?.length) return i18next.t('validation.required');
  if (!timeoutRegex.test(timeout))
    return i18next.t(
      'management_menu.asset_wizard.sensor.validation.timeout_format',
    );

  const numberPart = Number.parseInt(timeout.match(numberPartRegex)?.[0], 10);
  if (numberPart > 999) {
    return i18next.t(
      'management_menu.asset_wizard.sensor.validation.timeout_value',
    );
  }
};

export const sensorValidation = (values, sensorTypes) => {
  const errors = {};
  if ('id' in values) {
    if (!values.id.length) {
      errors.id = i18next.t('validation.required');
    } else {
      const idError = validateSensorId(values.id);
      if (idError !== undefined) errors.id = idError;
    }
  }

  if (!values.name.length) errors.name = i18next.t('validation.required');
  if (!values.user_group.length)
    errors.user_group = i18next.t('validation.required');
  if ('asset_type' in values && !values.asset_type.length)
    errors.asset_type = i18next.t('validation.required');
  if (!values.sensor_type.length)
    errors.sensor_type = i18next.t('validation.required');

  if ('timeout' in values) {
    const timeoutError = validateTimeout(values.timeout);
    if (timeoutError !== undefined) errors.timeout = timeoutError;
  }

  const selectedType = sensorTypes.find(st => st.value === values.sensor_type);
  if (selectedType?.measuring_mode === 'digital')
    validateBinaryIndicators({ values, errors });
  if (selectedType?.measuring_mode === 'threshold')
    validateFullThresholds(values, errors);
  if (selectedType?.measuring_mode === 'streaming') {
    if (!values.threshold.url.length) {
      errors.threshold ??= {};
      errors.threshold.url = i18next.t('validation.required');
    }
  }

  if (values.parameter && !parameterRegex.test(values.parameter))
    errors.parameter = i18next.t(
      'management_menu.asset_wizard.sensor.validation.parameter_format',
    );

  if (
    selectedType?.label === 'CorGPS' &&
    values?.threshold?.nimbelinkDeviceUpdate
  ) {
    const ajv = new Ajv({ strict: false, allErrors: true, messages: false });
    addFormats(ajv);
    const validate = ajv.compile(nimbelinkConfigSchema);
    const valid = validate(values.threshold.nimbelinkDeviceUpdate);

    if (!valid) {
      errors.threshold ??= {};
      errors.threshold.nimbelinkDeviceUpdate = i18next.t(
        'validation.invalid_json',
      );
    }
  }

  return errors;
};

export const partTypeValidation = values => {
  const errors = {};
  if (!values.name.length) errors.name = i18next.t('validation.required');
  if (!values.user_group.length)
    errors.user_group = i18next.t('validation.required');

  validateFullThresholds(values, errors, true);

  return errors;
};

export const partValidation = values => {
  const errors = {};
  const parsedValue = Number.parseInt(values.value, 10);
  const parsedLimitValue = Number.parseInt(values.limit_value, 10);

  if (!values.name.length) errors.name = i18next.t('validation.required');
  if (!values.user_group.length)
    errors.user_group = i18next.t('validation.required');
  if (!values.part_type.length)
    errors.part_type = i18next.t('validation.required');
  if (!values.asset_type.length)
    errors.asset_type = i18next.t('validation.required');

  if (!Number.isFinite(parsedValue))
    errors.value = i18next.t('validation.required');
  else if (parsedValue < 0)
    errors.value = i18next.t(
      'management_menu.asset_wizard.part.validation.negative',
    );
  else if (Number.isFinite(parsedLimitValue) && parsedValue > parsedLimitValue)
    errors.value = i18next.t(
      'management_menu.asset_wizard.part.validation.higher_than_limit',
    );

  if (!Number.isFinite(parsedLimitValue))
    errors.limit_value = i18next.t('validation.required');
  else if (parsedLimitValue < 0)
    errors.limit_value = i18next.t(
      'management_menu.asset_wizard.part.validation.negative',
    );
  validateFullThresholds(values, errors, true);
  return errors;
};

export const sensorTypeValidation = values => {
  const errors = {};
  if (!values.unit_type) errors.unit_type = i18next.t('validation.required');
  if (!values.name) errors.name = i18next.t('validation.required');
  if (!values.user_group.length)
    errors.user_group = i18next.t('validation.required');
  if (!values.unit) errors.unit = i18next.t('validation.required');
  switch (values.measuring_mode) {
    case 'digital':
      values.threshold?.['0'] &&
        values.threshold?.['1'] &&
        validateBinaryIndicators({ values, errors });
      break;
    case 'threshold':
      values.threshold && validateFullThresholds(values, errors);
      break;
    default:
      break;
  }

  return errors;
};
